import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { getImageUrl } from '../utils/imageUtils';
import {
    Container,
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    IconButton,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Menu,
    MenuItem,
    ListItemIcon
} from '@mui/material';
import {
    Search as SearchIcon,
    WhatsApp as WhatsAppIcon,
    Person as PersonIcon,
    LocalHospital as DoctorIcon,
    MedicalServices as NurseIcon,
    FamilyRestroom as FamilyIcon,
    ExpandMore as ExpandMoreIcon,
    ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';

const ContactsPage = () => {
    const { t } = useTranslation("global");
    const axiosPrivate = useAxiosPrivate();

    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dati
    const [elderly, setElderly] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [nurses, setNurses] = useState([]);

    // Menu dropdown parenti
    const [familyMenuAnchor, setFamilyMenuAnchor] = useState(null);
    const [selectedElderFamily, setSelectedElderFamily] = useState([]);

    // Fetch dati
    useEffect(() => {
        fetchAllContacts();
    }, []);

    const fetchAllContacts = async () => {
        setLoading(true);
        setError(null);
        try {
            const [elderlyRes, doctorsRes, nursesRes] = await Promise.all([
                axiosPrivate.get('/elderly'),
                axiosPrivate.get('/doctors'),
                axiosPrivate.get('/nurses')
            ]);

            setElderly(elderlyRes.data || []);
            setDoctors(doctorsRes.data || []);
            setNurses(nursesRes.data || []);
        } catch (err) {
            console.error('Errore nel caricamento dei contatti:', err);
            setError(`Errore nel caricamento dei contatti: ${err.response?.status || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Funzione per formattare numero telefono per WhatsApp
    const formatPhoneForWhatsApp = (phone) => {
        if (!phone) return null;
        // Rimuovi spazi, trattini, parentesi
        let cleaned = phone.replace(/[\s\-\(\)]/g, '');
        // Se inizia con 0, sostituisci con +39
        if (cleaned.startsWith('0')) {
            cleaned = '+39' + cleaned.substring(1);
        }
        // Se non ha prefisso, aggiungi +39
        if (!cleaned.startsWith('+')) {
            cleaned = '+39' + cleaned;
        }
        return cleaned;
    };

    // Apri WhatsApp
    const openWhatsApp = (phone, name) => {
        const formattedPhone = formatPhoneForWhatsApp(phone);
        if (!formattedPhone) {
            alert('Numero di telefono non disponibile');
            return;
        }
        const message = encodeURIComponent(`Ciao ${name}`);
        const url = `https://wa.me/${formattedPhone}?text=${message}`;
        window.open(url, '_blank');
    };

    // Filtra per ricerca
    const filterBySearch = (items, fields) => {
        if (!searchQuery) return items;
        return items.filter(item => 
            fields.some(field => 
                item[field]?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    };

    // Tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSearchQuery(''); // Reset ricerca
    };

    // Gestione menu dropdown parenti
    const handleOpenFamilyMenu = (event, familyMembers) => {
        setFamilyMenuAnchor(event.currentTarget);
        setSelectedElderFamily(familyMembers || []);
    };

    const handleCloseFamilyMenu = () => {
        setFamilyMenuAnchor(null);
        setSelectedElderFamily([]);
    };

    const handleContactFamily = (member) => {
        openWhatsApp(member.mobile_family_members, member.family_members_name);
        handleCloseFamilyMenu();
    };

    // Render anziani con dropdown parenti
    const renderElderlyList = () => {
        const filtered = filterBySearch(elderly, ['name', 'surname', 'room']);

        if (filtered.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                        {searchQuery ? 'Nessun anziano trovato' : 'Nessun anziano registrato'}
                    </Typography>
                </Box>
            );
        }

        return (
            <List>
                {filtered.map((elder, index) => (
                    <React.Fragment key={elder.elderly_id}>
                        {index > 0 && <Divider />}
                        <ListItem
                            sx={{
                                py: 2,
                                px: 2,
                                '&:hover': { 
                                    bgcolor: (theme) => theme.palette.action.hover,
                                    transition: 'background-color 0.2s'
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    src={elder.avatar ? getImageUrl(elder.avatar) : undefined}
                                    sx={{
                                        bgcolor: 'primary.main',
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    {!elder.avatar && <PersonIcon />}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {elder.name} {elder.surname}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {elder.room && (
                                            <Chip
                                                label={`Camera ${elder.room}`}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: (theme) => theme.palette.primary.light,
                                                    color: (theme) => theme.palette.primary.dark,
                                                    fontWeight: 500
                                                }}
                                            />
                                        )}
                                        {elder.mobile_phone && (
                                            <Chip
                                                icon={<WhatsAppIcon sx={{ color: '#25D366 !important' }} />}
                                                label={elder.mobile_phone}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    borderColor: '#25D366',
                                                    color: '#25D366'
                                                }}
                                            />
                                        )}
                                        {elder.family_members && elder.family_members.length > 0 && (
                                            <Chip
                                                icon={<FamilyIcon />}
                                                label={`${elder.family_members.length} ${elder.family_members.length === 1 ? 'parente' : 'parenti'}`}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: (theme) => theme.palette.info.light,
                                                    color: (theme) => theme.palette.info.dark
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {/* Bottone WhatsApp anziano */}
                                {elder.mobile_phone && (
                                    <IconButton
                                        onClick={() => openWhatsApp(elder.mobile_phone, elder.name)}
                                        sx={{
                                            bgcolor: '#25D366',
                                            color: 'white',
                                            '&:hover': { 
                                                bgcolor: '#128C7E',
                                                transform: 'scale(1.05)',
                                                transition: 'all 0.2s'
                                            }
                                        }}
                                    >
                                        <WhatsAppIcon />
                                    </IconButton>
                                )}
                                
                                {/* Bottone dropdown parenti */}
                                {elder.family_members && elder.family_members.length > 0 && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowDropDownIcon />}
                                            onClick={(e) => handleOpenFamilyMenu(e, elder.family_members)}
                                            sx={{
                                                borderColor: (theme) => theme.palette.secondary.main,
                                                color: (theme) => theme.palette.secondary.main,
                                                '&:hover': {
                                                    borderColor: (theme) => theme.palette.secondary.dark,
                                                    bgcolor: (theme) => theme.palette.secondary.light
                                                }
                                            }}
                                        >
                                            Contatta Parenti
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    // Render dottori
    const renderDoctorsList = () => {
        const filtered = filterBySearch(doctors, ['doctor_name', 'doctor_surname', 'doctor_specialization']);

        if (filtered.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                        {searchQuery ? 'Nessun dottore trovato' : 'Nessun dottore registrato'}
                    </Typography>
                </Box>
            );
        }

        return (
            <List>
                {filtered.map((doctor, index) => (
                    <React.Fragment key={doctor.doctor_id}>
                        {index > 0 && <Divider />}
                        <ListItem
                            sx={{
                                py: 2,
                                px: 2,
                                '&:hover': { 
                                    bgcolor: (theme) => theme.palette.action.hover,
                                    transition: 'background-color 0.2s'
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    src={doctor.avatar ? getImageUrl(doctor.avatar) : undefined}
                                    sx={{
                                        bgcolor: (theme) => theme.palette.info.main,
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    {!doctor.avatar && <DoctorIcon />}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Dr. {doctor.doctor_name} {doctor.doctor_surname}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ mt: 1 }}>
                                        {doctor.doctor_specialization && (
                                            <Chip
                                                label={doctor.doctor_specialization}
                                                size="small"
                                                sx={{ 
                                                    mr: 1, 
                                                    mb: 1,
                                                    bgcolor: (theme) => theme.palette.info.light,
                                                    color: (theme) => theme.palette.info.dark,
                                                    fontWeight: 500
                                                }}
                                            />
                                        )}
                                        {doctor.mobile && (
                                            <Chip
                                                icon={<WhatsAppIcon sx={{ color: '#25D366 !important' }} />}
                                                label={doctor.mobile}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    mb: 1,
                                                    borderColor: '#25D366',
                                                    color: '#25D366'
                                                }}
                                            />
                                        )}
                                        {doctor.phone && !doctor.mobile && (
                                            <Chip
                                                icon={<WhatsAppIcon sx={{ color: '#25D366 !important' }} />}
                                                label={doctor.phone}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    mb: 1,
                                                    borderColor: '#25D366',
                                                    color: '#25D366'
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                            {(doctor.mobile || doctor.phone) && (
                                <IconButton
                                    onClick={() => openWhatsApp(doctor.mobile || doctor.phone, `Dr. ${doctor.doctor_name}`)}
                                    sx={{
                                        bgcolor: '#25D366',
                                        color: 'white',
                                        '&:hover': { 
                                            bgcolor: '#128C7E',
                                            transform: 'scale(1.05)',
                                            transition: 'all 0.2s'
                                        }
                                    }}
                                >
                                    <WhatsAppIcon />
                                </IconButton>
                            )}
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    // Render infermiere
    const renderNursesList = () => {
        const filtered = filterBySearch(nurses, ['name', 'surname', 'role']);

        if (filtered.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                        {searchQuery ? 'Nessuna infermiera trovata' : 'Nessuna infermiera registrata'}
                    </Typography>
                </Box>
            );
        }

        return (
            <List>
                {filtered.map((nurse, index) => (
                    <React.Fragment key={nurse.nurse_id}>
                        {index > 0 && <Divider />}
                        <ListItem
                            sx={{
                                py: 2,
                                px: 2,
                                '&:hover': { 
                                    bgcolor: (theme) => theme.palette.action.hover,
                                    transition: 'background-color 0.2s'
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar
                                    src={nurse.avatar ? getImageUrl(nurse.avatar) : undefined}
                                    sx={{
                                        bgcolor: (theme) => theme.palette.secondary.main,
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    {!nurse.avatar && <NurseIcon />}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {nurse.name} {nurse.surname}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ mt: 1 }}>
                                        {nurse.role && (
                                            <Chip
                                                label={nurse.role}
                                                size="small"
                                                sx={{ 
                                                    mr: 1, 
                                                    mb: 1,
                                                    bgcolor: (theme) => theme.palette.secondary.light,
                                                    color: (theme) => theme.palette.secondary.dark,
                                                    fontWeight: 500
                                                }}
                                            />
                                        )}
                                        {nurse.mobile && (
                                            <Chip
                                                icon={<WhatsAppIcon sx={{ color: '#25D366 !important' }} />}
                                                label={nurse.mobile}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    mb: 1,
                                                    borderColor: '#25D366',
                                                    color: '#25D366'
                                                }}
                                            />
                                        )}
                                        {!nurse.is_present && (
                                            <Chip
                                                label="Assente"
                                                size="small"
                                                sx={{ 
                                                    ml: 1,
                                                    bgcolor: (theme) => theme.palette.error.light,
                                                    color: (theme) => theme.palette.error.dark,
                                                    fontWeight: 500
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                            {nurse.mobile && (
                                <IconButton
                                    onClick={() => openWhatsApp(nurse.mobile, nurse.name)}
                                    sx={{
                                        bgcolor: '#25D366',
                                        color: 'white',
                                        '&:hover': { 
                                            bgcolor: '#128C7E',
                                            transform: 'scale(1.05)',
                                            transition: 'all 0.2s'
                                        }
                                    }}
                                >
                                    <WhatsAppIcon />
                                </IconButton>
                            )}
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
            <Paper elevation={3} sx={{ overflow: 'hidden' }}>
                {/* Header */}
                <Box sx={{ 
                    p: 3, 
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white' 
                }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        📞 Rubrica Contatti
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                        Contatti WhatsApp per anziani, parenti, dottori e infermiere
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            '& .MuiTab-root': {
                                fontWeight: 600,
                                fontSize: '1rem',
                                py: 2
                            }
                        }}
                    >
                        <Tab
                            label={`Anziani (${elderly.length})`}
                            icon={<PersonIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label={`Dottori (${doctors.length})`}
                            icon={<DoctorIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label={`Infermiere (${nurses.length})`}
                            icon={<NurseIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Search Bar */}
                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Cerca per nome, cognome..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                </Box>

                {/* Content */}
                <Box sx={{ minHeight: 400 }}>
                    {activeTab === 0 && renderElderlyList()}
                    {activeTab === 1 && renderDoctorsList()}
                    {activeTab === 2 && renderNursesList()}
                </Box>
            </Paper>

            {/* Menu dropdown parenti */}
            <Menu
                anchorEl={familyMenuAnchor}
                open={Boolean(familyMenuAnchor)}
                onClose={handleCloseFamilyMenu}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        minWidth: 300,
                        maxWidth: 400,
                        mt: 1
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1, bgcolor: (theme) => theme.palette.grey[100] }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FamilyIcon fontSize="small" />
                        Seleziona parente da contattare:
                    </Typography>
                </Box>
                <Divider />
                {selectedElderFamily.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            Nessun parente con numero di telefono
                        </Typography>
                    </MenuItem>
                ) : (
                    selectedElderFamily.map((member) => (
                        <MenuItem
                            key={member.id_family_members}
                            onClick={() => handleContactFamily(member)}
                            disabled={!member.mobile_family_members}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: '#25D36610'
                                }
                            }}
                        >
                            <ListItemIcon>
                                <Avatar
                                    sx={{
                                        bgcolor: 'secondary.main',
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    <FamilyIcon />
                                </Avatar>
                            </ListItemIcon>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {member.family_members_name} {member.family_members_surname}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                                    {member.degree_of_kinship && (
                                        <Chip
                                            label={member.degree_of_kinship}
                                            size="small"
                                            sx={{ 
                                                height: 20,
                                                fontSize: '0.7rem',
                                                bgcolor: (theme) => theme.palette.secondary.light,
                                                color: (theme) => theme.palette.secondary.dark
                                            }}
                                        />
                                    )}
                                    {member.mobile_family_members ? (
                                        <Typography variant="caption" sx={{ color: '#25D366', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                            <WhatsAppIcon sx={{ fontSize: 14 }} />
                                            {member.mobile_family_members}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            Numero non disponibile
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            {member.mobile_family_members && (
                                <WhatsAppIcon sx={{ color: '#25D366', ml: 1 }} />
                            )}
                        </MenuItem>
                    ))
                )}
            </Menu>
        </Container>
    );
};

export default ContactsPage;
